<template lang="pug">
    v-form(
        v-if="currentUser"
        :disabled="!validCustomer"
    )
        v-alert(dense outlined type='error' v-if="!validCustomer") In order to schedule a new appointment, a valid customer must be present.
        v-alert(dense outlined type='error' v-if="showDuplicateAppointmentAlert") An appointment already exists for this customer or vin at the selected date time.


        v-menu(
            v-model='datePickerMenu'
            :close-on-content-click='false'
            transition='scale-transition'
            offset-y
            max-width='290px'
            min-width='290px'
        )
            template(v-slot:activator='{ on, attrs }')
                v-text-field(
                    v-model='formatAppointmentDate'
                    label='Date *'
                    prepend-icon='mdi-calendar'
                    readonly
                    v-bind='attrs'
                    v-on='on'
                    :disabled="disableAppointmentDatetimeFields"
                )
            v-date-picker(
                v-model='appointment.appointment_datetime_date'
                no-title
                @input='datePickerMenu = false'
                :min="minDate"
            )
        Timepicker(
            v-if="accountMinutes === 'anytime'"
            label="Time *"
            :on-change="onChangeTimepicker"
            :date-value="appointment.appointment_datetime_date"
            :value="appointment.appointment_datetime_time"
            :disable-input="disableAppointmentDatetimeFields"
            :appointment-scheduling-hour="appointmentSchedulingHour"
        )
        AppointmentTimepicker(
            v-else
            label="Time *"
            :on-change="onChangeTimepicker"
            :date-value="appointment.appointment_datetime_date"
            :value="appointment.appointment_datetime_time"
            :disable-input="disableAppointmentDatetimeFields"
            :appointment-scheduling-hour="appointmentSchedulingHour"
            :accountMinutes="accountMinutes"
            :dashboard-default-date="dashboardDefaultDate"
        )

        v-select(
            v-model='appointment.time_zone_id'
            :items='currentUser.time_zones'
            label='Time Zone *'
            item-text="name"
            item-value="id"
            :disabled="disableAppointmentDatetimeFields"
        )

        v-select(
            v-if="appointmentTypes.length > 0"
            v-model='appointment.appointment_type_id'
            :items='appointmentTypes'
            label='Type'
            item-text="name"
            item-value="appointment_type_id"
        )

        v-text-field(
            v-if="isAppointmentDashboard"
            v-model="appointment.appointment_number"
            label='Appointment Number'
            type='text'
        )

        v-select(
            v-if="isAppointmentDashboard"
            v-model='appointment.user_id'
            :items='users'
            label='Agent'
            item-text="user_full_name"
            item-value="user_id"
            clearable
        )

        v-row(v-if="showVehiclesDropdown")
            v-col(cols='12')
                .text-right
                    CustomerVehicleFormDialog(
                        :create-customer-vehicle="createCustomerVehicle"
                    )

        v-select(
            v-if="showVehiclesDropdown"
            v-model="selectedCustomerVehicle"
            label='Vehicle'
            :items='customerVehicles'
            :item-text="vehicleLabel"
            return-object
            style="margin-top: -20px;"
            clearable
        )

        v-text-field(
            v-if="renderDeclineBonusesField"
            v-model="appointment.decline_bonuses"
            label="Decline Bonuses"
            type="number"
            min="0"
        )

        v-text-field(
            v-if="renderRecallFields"
            v-model="appointment.recall_bonuses"
            label='Recalls'
            type='number'
            min='0'
        )

        v-select(
            v-if="renderLeaseFields"
            v-model='appointment.vehicle_access'
            :items='vehicleAccesses'
            label='Vehicle Access'
            item-text="key"
            item-value="value"
        )

        v-select(
            v-if="renderLeaseFields"
            v-model='appointment.delivery_location'
            :items='deliveryLocations'
            label='Delivery Location'
            item-text="key"
            item-value="value"
        )

        v-text-field(
            v-if="renderLeaseFields"
            v-model="appointment.delivery_address"
            label='Delivery Address'
            type='text'
        )

        v-text-field(
            v-if="renderLeaseFields"
            v-model="appointment.customer_phone_number"
            label='Customer Phone Number'
            type='text'
        )

        v-text-field(
            v-if="renderLeaseFields"
            v-model="appointment.license_plate"
            label='License Plate'
            type='text'
        )

        v-text-field(
            v-if="renderLeaseFields"
            v-model="appointment.demo_vehicle"
            label='Demo Vehicle'
            type='text'
        )

        v-textarea(
            v-model="appointment.special_instructions"
            label='Special Instructions'
            rows="1"
            auto-grow
            clearable
            counter
            outlined
            dense
            :disabled="validIntegrationAppointmentDetails"
            prepend-inner-icon="mdi-text"
        )

        v-snackbar(
            v-model='snackbarOpen'
            :color='snackbar.color'
            :timeout="snackbar.timeout"
            top
        )
            | {{ snackbar.text }}

</template>

<script lang="ts">
    import Vue from 'vue';
    import VolieState  from "@/store";

    // Components
    import Timepicker from "@/components/shared/timepicker.vue";
    import AppointmentTimepicker from "@/components/shared/appointments/timepicker.vue";
    import CustomerVehicleFormDialog from "@/components/customer_vehicles/form_dialog.vue";

    // Data
    import { CreateCustomerVehicle, GetCustomerVehicles, GetOrganizationOrganizationUsers, GetAppointments, GetCampaignCampaignTypes } from "@/data";

    // Helpers
    import { time12ToMilitary } from "@/helpers/general";

    export default Vue.extend({
        data() {
            return {
                appointment: null,
                campaignCampaignTypes: [],
                customerVehicles: [],
                datePickerMenu: false,
                deliveryLocations: [
                    { key: "Home", value: "Home" },
                    { key: "Office", value: "Office" },
                    { key: "Other", value: "Other" },
                ],
                existingAppointmentMatches: [],
                minDate: null,
                selectedCustomerVehicle: null,
                snackbar: {
                    timeout: 2000,
                },
                snackbarOpen: false,
                users: [],
                vehicleAccesses: [
                    { key: "Pick Up", value: 1 },
                    { key: "Drop Off", value: 2 },
                ],
            }
        },
        methods: {
            createCustomerVehicle(vehicleOptions) {
                if (!(this.validCustomer && vehicleOptions?.vehicle_make?.length > 0 && vehicleOptions?.vehicle_model?.length > 0 && vehicleOptions?.vehicle_model_year?.length > 0)) {
                    return;
                }

                // Set options
                const options = {
                    account_id:                  this.customer.account_id,
                    customer_id:                 this.customer.customer_id,
                    organization_id:             this.customer.organization_id,
                    vehicle_make:                vehicleOptions.vehicle_make,
                    vehicle_model:               vehicleOptions.vehicle_model,
                    vehicle_model_year:          vehicleOptions.vehicle_model_year,
                    vehicle_trim:                vehicleOptions.vehicle_trim,
                    vin:                         vehicleOptions.vin,
                }

                CreateCustomerVehicle(options).then((response) => {
                    if (response?.Data?.customer_vehicle?.customer_vehicle_id > 0) {
                        this.showSnackbar("green", "Successfully created customer vehicle");
                        this.handleCreateCustomerVehicle(response.Data.customer_vehicle);
                        const newVehicle = response.Data.customer_vehicle;
                        this.$root.$emit('customer-vehicle-created', newVehicle);
                    } else {
                        if (response?.Error === "Invalid vin") {
                            this.showSnackbar("red", "Invalid vin");
                        } else {
                            this.showSnackbar("red", "Failed to create customer vehicle");
                        }
                    }
                })
            },
            formatDate (date) {
                if (!date) {
                    return null;
                }

                const [year, month, day] = date.split('-');
                return `${month}/${day}/${year}`;
            },
            getAppointments(options) {
                GetAppointments(options).then((response) => {
                    if (response?.Data?.appointments?.length > 0) {
                        this.existingAppointmentMatches = response.Data.appointments;
                        // disable save and next button if required
                        this.setAppointmentIsUniqueIfRequired(!(this.duplicateAppointmentActionForAccount === 'alert_and_prevent'));
                    } else {
                        // set if no matches (we set to false before fetch so button would be disabled while we fetched)
                        this.setAppointmentIsUniqueIfRequired(true);
                    }
                });
            },
            getCampaignCampaignTypes() {
                if (!(this.customer?.campaign_id > 0)) {
                    return;
                }
                const options = {
                    campaign_id: this.customer.campaign_id
                }

                GetCampaignCampaignTypes(options).then((response) => {
                    if (response?.Data?.CampaignCampaignTypes.length > 0) {
                        this.campaignCampaignTypes = response.Data.CampaignCampaignTypes;
                        this.setDefaultAppointmentType();
                    }
                });
            },
            getCustomerVehicles() {
                if (!this.customer?.account_id || !this.customer?.customer_id || !this.customer?.organization_id) {
                    return;
                }

                const options = {
                    account_id:                  this.customer.account_id,
                    customer_id:                 this.customer.customer_id,
                    organization_id:             this.customer.organization_id,
                }

                GetCustomerVehicles(options).then((response) => {
                    if (response?.Data?.customer_vehicles?.length > 0) {
                        this.customerVehicles = response.Data.customer_vehicles;
                    }
                });

            },
            getOrganizationOrganizationUsers() {
                if (!this.customer.organization_id) {
                    return;
                }

                GetOrganizationOrganizationUsers(this.customer.organization_id.toString()).then((response) => {
                    if (response?.Data?.organization_organization_users?.length > 0) {
                        this.users = response.Data.organization_organization_users;
                    }
                }).catch((error) => {
                    console.error('Error fetching organization users:', error);
                });
            },
            handleCreateCustomerVehicle(customerVehicle) {
                this.customerVehicles.push(customerVehicle);
                this.selectedCustomerVehicle = customerVehicle;
            },
            handleDuplicateAppointmentCheck() {

                // reset the existing appointment matches
                this.existingAppointmentMatches = [];

                // if we don't have the datetime, or either the customer or vehicle, return
                if (!this.appointment.appointment_datetime_date || !this.appointment.appointment_datetime_time || (!this.customer.customer_id && !this.appointment.vehicle_id)) {
                    this.setAppointmentIsUniqueIfRequired(true); // reset
                    return;
                }

                // otherwise, run a check!
                const options = {
                    appointment_datetime: this.appointment?.appointment_datetime || null,
                    appointment_datetime_date: this.appointment?.appointment_datetime_date || null,
                    appointment_datetime_time: this.appointment?.appointment_datetime_time || null,
                    customer_id: this.customer?.customer_id || 0,
                    duplicate_appointment_check: true,
                    organization_id: this.customer?.organization_id || 0,
                    time_zone_id: this.appointment?.time_zone_id || 0,
                    vehicle_vin: this.selectedCustomerVehicle?.vin || "",
                    vehicle_id: this.selectedCustomerVehicle?.vehicle_id || 0,
                }
                this.getAppointments(options);
            },
            handleInitialGetCustomerVehicles() {
                if (!this.currentCampaignCustomerCustomerVehicleId || this.customerVehicles?.length === 0) {
                    return;
                }

                const idx = this.customerVehicles.findIndex(i => i.customer_vehicle_id === this.currentCampaignCustomerCustomerVehicleId);
                if (idx >= 0) {
                    this.selectedCustomerVehicle = this.customerVehicles[idx];
                }
            },
            initAppointment() {
                this.appointment = {
                    appointment_datetime_date: this.dashboardDefaultDate !== undefined ? this.dashboardDefaultDate : null,
                    appointment_datetime_time: null,
                    appointment_type_id: null,
                    attempt_key: this.currentAgent?.attempt_key,
                    customer_phone_number: null,
                    customer_vehicle_id: null,
                    decline_bonuses: '0',
                    delivery_address: null,
                    delivery_location: null,
                    demo_vehicle: null,
                    license_plate: null,
                    recall_bonuses: '0',
                    special_instructions: null,
                    time_zone_id: null,
                    vehicle_access: null,
                    vehicle_id: null,
                }

                if (this.isAppointmentDashboard) {
                    this.appointment.appointment_number = null;
                    this.appointment.user_id = this.currentUser?.user_id || null;
                }

                // Set autobuy fields if already present
                if (!!this.autobuyCommunicationDisposition) {
                    this.appointment.appointment_datetime_date = this.autobuyCommunicationDisposition.appointment_datetime_date;
                    this.appointment.appointment_datetime_time = this.autobuyCommunicationDispositionBuyerAvailability;
                }

                // set integration fields if present
                if (!!this.integrationAppointmentDetails && this.integrationAppointmentDetails.appointment_id > 0) {
                    this.appointment.appointment_datetime_date = this.integrationAppointmentDetails.appointment_datetime_date;
                    this.appointment.appointment_datetime_time = this.integrationAppointmentDetails.appointment_datetime_time;
                    this.appointment.time_zone_id = this.integrationAppointmentDetails.time_zone_id;
                    this.appointment.special_instructions = this.integrationAppointmentDetails.special_instructions;
                }

                // set scheduled from
                if (this.isAppointmentDashboard) {
                    this.appointment.source = "appointment_dashboard";
                } else if (this.customerProfileAppointment) {
                    this.appointment.source = "customer_profile";
                } else if (this.customer?.campaign_customer_id > 0) {
                    this.appointment.source = "communication_center";
                }
            },
            onVehicleCreated(newVehicle) {
                this.getCustomerVehicles();
                this.$nextTick(() => {
                    this.selectedCustomerVehicle = newVehicle;
                });
            },
            onChange() {
                // If we need to use, call function
                if (this.useOnChange === true) {
                    this.onAppointmentChange(this.appointment);
                }

                if (this.duplicateAppointmentActionForAccount !== 'allow') {
                    // set to false so button isn't enabled while we're fetching
                    this.setAppointmentIsUniqueIfRequired(false);
                    // run the check
                    this.handleDuplicateAppointmentCheck();
                }
            },
            onChangeTimepicker(time) {
                this.appointment.appointment_datetime_time = time;
            },
            onVehicleDeleted() {
                this.getCustomerVehicles();
            },
            setDefaultAppointmentType() {
                if (!this.isOkayToDefaultAppointmentType) {
                    return;
                }

                const primaryCampaign = this.campaignCampaignTypes[0];
                if (!primaryCampaign) {
                    return;
                }

                // Score each appointment type by how well it matches each campaign type
                let scoredMatches = [];
                for (const campaignType of this.campaignCampaignTypes) {
                    // If we matched on the primary campaign type, skip the rest
                    if (!campaignType.is_primary && scoredMatches.some(match => match.campaignType.is_primary && match.score > 0)) {
                        break;
                    }

                    scoredMatches = scoredMatches.concat(this.appointmentTypes.map(appointment => {
                        let score = 0;
                        const campaignTypeName = campaignType.campaign_type_name?.toLowerCase() || '';
                        const appointmentTypeName = appointment.name?.toLowerCase() || '';
                        const campaignTypeType = campaignType.type?.toLowerCase() || '';
                        const appointmentTypeType = appointment.type?.toLowerCase() || '';
                        const agentFirstName = this.currentUser?.first_name?.toLowerCase() || '';
                        const agentLastName = this.currentUser?.last_name?.toLowerCase() || '';

                        // Name and type match
                        if ((campaignTypeName === appointmentTypeName || campaignTypeType === appointmentTypeName) && campaignTypeType === appointmentTypeType) {
                            score += 100;
                        }
                        // Name match
                        else if (campaignTypeName === appointmentTypeName || campaignTypeType === appointmentTypeName) {
                            score += 60;
                        }
                        // Type match
                        else if (campaignTypeType === appointmentTypeType && campaignTypeType !== '') {
                            score += 40;
                        }

                        // Name match on agent name
                       if (appointmentTypeName.includes(agentFirstName) && appointmentTypeName.includes(agentLastName)) {
                            score += 61;
                        } else if (appointmentTypeName.includes(agentFirstName) || appointmentTypeName.includes(agentLastName)) {
                            score += 60;
                        }

                        // Partial name match (match on campaign name or type)
                        if (score < 100 && campaignTypeName && appointmentTypeName &&
                                (appointmentTypeName.includes(campaignTypeName) || campaignTypeName.includes(appointmentTypeName) || campaignTypeType.includes(appointmentTypeName) || appointmentTypeName.includes(campaignTypeType))) {
                            score += 30;
                        }

                        return { appointment, score, campaignType };
                    }));
                }

                scoredMatches.sort((a, b) => b.score - a.score);
                
                const bestMatch = scoredMatches[0];
                if (bestMatch && bestMatch.score > 0 && bestMatch.appointment.appointment_type_id > 0) {
                    this.appointment.appointment_type_id = bestMatch.appointment.appointment_type_id;
                }
            },
            setDefaultTimeZone() {

                // Set default time zone ID
                if (this.timeZoneId > 0 && this.currentUser?.time_zones?.length > 0) {
                    const idx = this.currentUser.time_zones.findIndex(i => i.id === this.timeZoneId);
                    if (idx >= 0) {
                        this.appointment.time_zone_id = this.timeZoneId;
                    }
                }

                // If still invalid, default to eastern
                if (!(this.appointment.time_zone_id > 0)) {
                    this.appointment.time_zone_id = 43;
                }
            },
            setMinDate() {
                const tzoffset = (new Date()).getTimezoneOffset() * 60000; //offset in milliseconds
                this.minDate = (new Date(Date.now() - tzoffset)).toISOString().slice(0, -1);
            },
            setRescheduleAppointmentFields() {

                // Verify reschedule is present
                if (!(this.rescheduleAppointment?.appointment_id > 0)) {
                    return;
                }

                // Set defaults
                this.appointment.appointment_datetime_date = this.rescheduleAppointment.appointment_datetime_date;
                this.appointment.appointment_datetime_time = this.rescheduleAppointment.appointment_datetime_time;
                this.appointment.appointment_type_id = this.rescheduleAppointment.appointment_type_id;
                this.appointment.special_instructions = this.rescheduleAppointment.special_instructions;

                // Lease fields
                this.appointment.customer_phone_number = this.rescheduleAppointment.customer_phone_number;
                this.appointment.delivery_address = this.rescheduleAppointment.delivery_address;
                this.appointment.delivery_location = this.rescheduleAppointment.delivery_location;
                this.appointment.demo_vehicle = this.rescheduleAppointment.demo_vehicle;
                this.appointment.license_plate = this.rescheduleAppointment.license_plate;
                this.appointment.vehicle_access = this.rescheduleAppointment.vehicle_access;

                // set integration fields if present
                if (!!this.integrationAppointmentDetails && this.integrationAppointmentDetails.appointment_id > 0) {
                    this.appointment.appointment_datetime_date = this.integrationAppointmentDetails.appointment_datetime_date;
                    this.appointment.appointment_datetime_time = this.integrationAppointmentDetails.appointment_datetime_time;
                    this.appointment.time_zone_id = this.integrationAppointmentDetails.time_zone_id;
                    this.appointment.special_instructions = this.integrationAppointmentDetails.special_instructions;
                }
            },
            showSnackbar(color, text) {
                if (color?.length > 0 && text?.length > 0) {
                    this.snackbar.color = color;
                    this.snackbar.text = text;
                    this.snackbarOpen = true;
                }
            },
        },
        computed: {
            accountMinutes() {
                return this.currentUser?.account?.appt_times_setting;
            },
            appointmentTypes() {
                return (this.currentUser?.account?.appointment_types || []);
            },
            autobuyCommunicationDispositionBuyerAvailability() {
                return this.autobuyCommunicationDisposition?.buyer_availability?.length > 0 ? time12ToMilitary(this.autobuyCommunicationDisposition?.buyer_availability) : null;
            },
            currentAgent() {
                return VolieState.currentAgent();
            },
            currentCampaignCustomerCustomerVehicleId() {
                return this.customer?.campaign_customer_active_id > 0 ? this.customer?.customer_vehicle_id : null;
            },
            currentUser() {
                return VolieState.currentUser();
            },
            duplicateAppointmentActionForAccount() {
                return this.currentUser?.account?.duplicate_appointment_action;
            },
            isAppointmentDashboard() {
                return !!this.dashboardDefaultDate;
            },
            disableAppointmentDatetimeFields() {
                return this.validAutobuyCommunicationDisposition || this.validIntegrationAppointmentDetails;
            },
            formatAppointmentDate() {
                return this.formatDate(this.appointment?.appointment_datetime_date);
            },
            isOkayToDefaultAppointmentType() {
                if (!this.currentUser?.account?.appointment_type_defaulting_enabled) {
                    return false;
                }

                // If we're rescheduling, don't try to set the appointment type
                if (this.rescheduleAppointment?.appointment_id > 0) {
                    return false;
                }
                
                if (!this.campaignCampaignTypes || this.campaignCampaignTypes.length === 0) {
                    return false;
                }
                
                if (!this.appointmentTypes || this.appointmentTypes.length === 0) {
                    return false;
                }


                return true;
            },
            renderDeclineBonusesField() {
                return this.currentUser?.account?.account_id === 1020;
            },
            renderLeaseFields() {
                return this.currentUser?.account?.account_id === 7; // account 7 only
            },
            renderRecallFields() {
                return this.currentUser?.account?.account_id === 1020;
            },
            showDuplicateAppointmentAlert() {
                return this.duplicateAppointmentActionForAccount !== 'allow' && this.existingAppointmentMatches?.length > 0;
            },
            validAutobuyCommunicationDisposition() {
                return !!this.autobuyCommunicationDisposition;
            },
            validIntegrationAppointmentDetails() {
                return !!this.integrationAppointmentDetails && this.integrationAppointmentDetails.appointment_id > 0;
            },
            vehicleLabel() {
                return (item) => {
                    // Check if all values are null/undefined/empty
                    if (!item.vehicle_make && !item.vehicle_model && !item.vehicle_model_year) {
                        return "Unspecified Vehicle";
                    }

                    // Create an array of the available values
                    const vehicleInfo = [];
                    if (item.vehicle_model_year) vehicleInfo.push(item.vehicle_model_year);
                    if (item.vehicle_make) vehicleInfo.push(item.vehicle_make);
                    if (item.vehicle_model) vehicleInfo.push(item.vehicle_model);

                    // Join the available values with spaces
                    const label = vehicleInfo.join(' ');
                    return item?.vin ? `${label} (VIN: ${item.vin})` : label;
                };
            },
        },
        components: {
            CustomerVehicleFormDialog,
            Timepicker,
            AppointmentTimepicker,
        },
        props: {
            appointmentSchedulingHour: Object,
            autobuyCommunicationDisposition: Object,
            customer: {
                type: Object,
                required: false
            },
            dashboardDefaultDate: {
                type: String,
                required: false,
                default: null
            },
            customerProfileAppointment: {
                type: Boolean,
                required: false,
                default: false
            },
            onAppointmentChange: Function,
            rescheduleAppointment: Object,
            showVehiclesDropdown: {
                type: Boolean,
                default: false
            },
            setAppointmentIsUniqueIfRequired: Function,
            integrationAppointmentDetails: Object,
            timeZoneId: Number,
            useOnChange: Boolean,
            validCustomer: Boolean,
        },
        watch: {
            appointment: {
                deep: true,
                handler() {
                    this.onChange();
                },
            },
            "autobuyCommunicationDisposition.appointment_datetime_date"() {
                this.appointment.appointment_datetime_date = this.autobuyCommunicationDisposition.appointment_datetime_date;
            },
            "autobuyCommunicationDisposition.buyer_availability"() {
                this.appointment.appointment_datetime_time = this.autobuyCommunicationDispositionBuyerAvailability;
            },
            customerVehicles() {
               this.handleInitialGetCustomerVehicles();
            },
            selectedCustomerVehicle: {
                handler(newValue) {
                    if (newValue) {
                        this.appointment.customer_vehicle_id = newValue.customer_vehicle_id;
                        this.appointment.vehicle_id = newValue.vehicle_id;
                    } else {
                        this.appointment.customer_vehicle_id = null;
                        this.appointment.vehicle_id = null;
                    }
                    this.onChange();
                }
            },
           'integrationAppointmentDetails': {
                handler(newValue) {
                    if (newValue.appointment_id > 0) {
                        this.initAppointment();
                    }
                },
                immediate: false,
                deep:true
            }
        },
        created() {
            this.initAppointment();
            this.getCampaignCampaignTypes();
            this.setDefaultTimeZone();
            this.setMinDate();
            this.getCustomerVehicles();

            if (this.isAppointmentDashboard) {
                this.getOrganizationOrganizationUsers();
            }

            // Set fields from reschedule is present
            this.setRescheduleAppointmentFields();
        },
        mounted() {
            this.$root.$on('customer-vehicle-deleted', this.onVehicleDeleted);
            this.$root.$on('customer-vehicle-created', this.onVehicleCreated);
        },
        destroyed() {
            this.$root.$off('customer-vehicle-deleted', this.onVehicleDeleted);
            this.$root.$off('customer-vehicle-created', this.onVehicleCreated);
        },
    });
</script>

<style lang="scss" scoped>
</style>
